import { Dialog } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/Button';
import { BillStatus, SHIPPING_FEE } from '../../../consts';
import { useSession } from '../../../context/SessionContext';
import { useSystemContext } from '../../../context/SystemContext/SystemContext';
import { useUserMenuContext } from '../../../context/UserMenuContext';
import useCreateOrder from '../../../hook/useCreateOrder';
import PAYMENT from '../../DeliveryInformation/components/Payment';
import PaymentNoticeDialog from '../../../components/PaymentNoticeDialog/PaymentNoticeDialog';
import { getPaymentNoticeAlerts } from '../../../utils/cancellationAlert';
import { calculateOrderItemsTotal } from '../../../utils';
import usePaymentMethod from '../../../hook/usePaymentMethod';

interface PaymentBacklogProps {
  orderDetail: any;
  multiOrder?: boolean;
}

const PaymentBacklog: React.FC<PaymentBacklogProps> = ({ orderDetail, multiOrder = false }) => {
  const { t } = useTranslation();
  const [openPayment, setOpenPayment] = useState(false);
  const [openPaymentNotice, setOpenPaymentNotice] = useState(false);
  const [openPaymentDelayWarning, setOpenPaymentDelayWarning] = useState(false);
  const [isSubmittingPaymentNotice, setIsSubmittingPaymentNotice] = useState(false);
  const { checkOrderV2, checkOrder } = useCreateOrder();
  const [openError, setOpenError] = useState(false);
  const [errorFood, setErrorFood] = useState<string>('');
  const [pendingCodeState, setPendingCodeState] = useState<'checking' | 'active' | 'processing'>('checking');
  const { checkSessionExpiry } = useSession();
  const { stadiumInfo } = useSystemContext();
  const { listRestaurantData, listRestaurantWithFood } = useUserMenuContext();
  const { checkPaymentStatus } = usePaymentMethod();
  const isPaymentPending = orderDetail?.bill_status === BillStatus.PAYMENT_PENDING;
  const isPreOrderPayment = orderDetail?.is_pre_order === true || String(orderDetail?.order_type || '').includes('pre_order');

  const [totalPrice] = useMemo(() => {
    if (!orderDetail) return [0];

    const totalPrice = calculateOrderItemsTotal(orderDetail);
    const formattedPrice = totalPrice + SHIPPING_FEE;
    return [formattedPrice];
  }, [orderDetail]);
  const paymentNoticeAlerts = useMemo(
    () =>
      getPaymentNoticeAlerts({
        order: orderDetail ?? {},
        restaurants: [...listRestaurantWithFood, ...listRestaurantData],
        language: stadiumInfo?.language,
      }),
    [listRestaurantData, listRestaurantWithFood, orderDetail, stadiumInfo?.language]
  );

  useEffect(() => {
    if (!isPaymentPending) {
      setPendingCodeState('checking');
      setOpenPaymentDelayWarning(false);
      return;
    }

    let cancelled = false;
    let requestInProgress = false;
    let latestPaymentStatus: string | undefined;
    setPendingCodeState('checking');
    setOpenPaymentDelayWarning(false);

    const pollPaymentStatus = async () => {
      if (cancelled || requestInProgress) return;

      requestInProgress = true;
      try {
        const result = await checkPaymentStatus(orderDetail.id);
        if (cancelled) return;
        latestPaymentStatus = result?.status;
        setPendingCodeState(result?.status === 'pending' ? 'active' : 'processing');
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to check payment status', error);
        setPendingCodeState('processing');
      } finally {
        requestInProgress = false;
      }
    };

    pollPaymentStatus();
    const paymentStatusInterval = window.setInterval(pollPaymentStatus, 1000);
    const paymentStatusTimeout = window.setTimeout(() => {
      window.clearInterval(paymentStatusInterval);
      if (!cancelled && latestPaymentStatus === 'captured') {
        setOpenPaymentDelayWarning(true);
      }
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(paymentStatusInterval);
      window.clearTimeout(paymentStatusTimeout);
    };
  }, [checkPaymentStatus, isPaymentPending, orderDetail.id, orderDetail?.payment_code_id]);

  const handlePlaceOrder = async () => {
    if (!multiOrder && checkSessionExpiry()) return;
    const isErrorOrder = multiOrder ? await checkOrderV2(orderDetail) : await checkOrder(orderDetail);
    if (isErrorOrder) {
      setOpenPayment(true);
    } else {
      const errorFoodItem = sessionStorage.getItem('ErrorFood');
      if (errorFoodItem) {
        setErrorFood(errorFoodItem);
      }
      setOpenError(true);
    }
  };

  const handleClosePayment = useCallback(() => {
    setOpenPayment(false);
  }, []);

  const handleCompletePayment = async () => {
    if (isSubmittingPaymentNotice) return;
    if (multiOrder && isPreOrderPayment && paymentNoticeAlerts.length > 0) {
      setOpenPaymentNotice(true);
      return;
    }

    setIsSubmittingPaymentNotice(true);
    try {
      await handlePlaceOrder();
    } finally {
      setIsSubmittingPaymentNotice(false);
    }
  };

  const handleSubmitPaymentNotice = async () => {
    if (isSubmittingPaymentNotice) return;

    setIsSubmittingPaymentNotice(true);
    try {
      setOpenPaymentNotice(false);
      await handlePlaceOrder();
    } finally {
      setIsSubmittingPaymentNotice(false);
    }
  };

  const handleCancelPaymentNotice = () => {
    if (isSubmittingPaymentNotice) return;
    setOpenPaymentNotice(false);
  };

  const handleCloseErrorModal = () => {
    setOpenError(false);
    sessionStorage.removeItem('ErrorFood');
  };

  return (
    <div className="flex flex-col gap-2 items-center">
      <p className="text-black-500 font-normal text-sm leading-5">{t('CONTINUE_PAY')}</p>
      {isPaymentPending ? (
        orderDetail?.payment_code_id && pendingCodeState === 'active' ? (
          <Button onClick={() => setOpenPayment(true)} type="primary">
            {t('CONTINUE_PAYMENT', { defaultValue: 'Continue payment' })}
          </Button>
        ) : (
          <p className="text-black-500 font-normal text-sm leading-5 text-center">
            {t('PAYMENT_PROCESSING_MESSAGE', { defaultValue: 'Payment is being processed. Please wait.' })}
          </p>
        )
      ) : (
        <Button onClick={handleCompletePayment} type="submit" disabled={isSubmittingPaymentNotice}>
          {t('COMPLETE_PAYMENT')}
        </Button>
      )}
      <PaymentNoticeDialog
        open={openPaymentNotice}
        onCancel={handleCancelPaymentNotice}
        onSubmit={handleSubmitPaymentNotice}
        isSubmitting={isSubmittingPaymentNotice}
        alerts={paymentNoticeAlerts}
      />
      <Dialog
        open={openPaymentDelayWarning}
        onClose={() => setOpenPaymentDelayWarning(false)}
        sx={{ '& .MuiPaper-root': { borderRadius: '12px', margin: '16px' } }}
      >
        <div className="p-6 flex flex-col gap-4 max-w-md">
          <p className="text-black-900 text-lg font-semibold leading-7">
            {t('PAYMENT_DELAY_TITLE', { defaultValue: 'Payment is taking longer than expected' })}
          </p>
          <p className="text-black-500 text-sm font-normal leading-5">
            {t('PAYMENT_DELAY_DESCRIPTION', {
              defaultValue:
                'Your payment may still be processing. Please do not create another order, as this could result in a duplicate payment.',
            })}
          </p>
          <Button onClick={() => setOpenPaymentDelayWarning(false)} type="primary">
            {t('OK', { defaultValue: 'OK' })}
          </Button>
        </div>
      </Dialog>
      <PAYMENT
        open={openPayment}
        onClose={handleClosePayment}
        totalPrice={totalPrice}
        orderId={orderDetail.id}
        restaurant_id={orderDetail.restaurant_id}
        existingCodeObjectId={isPaymentPending ? orderDetail?.payment_code_id : undefined}
      />
      <Dialog open={openError} onClose={handleCloseErrorModal} sx={{ '& .MuiPaper-root': { borderRadius: '12px' } }}>
        <div className="p-6 flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <p className="text-black-900 text-lg font-semibold leading-7">{errorFood ? `${errorFood} ${t('HAS_BEEN_DISABLE')}` : t('LISTRESTAURANT_MODAL_TITLE')}</p>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PaymentBacklog;
